import BaseRepository from './BaseRepository';
import AjaxService from '@services/AjaxService';

export default class FirmRepository extends BaseRepository {
  protected baseUrl = '/de/Schaden-Manager/Firma';

  public constructor(ajaxService: AjaxService) {
    super(ajaxService);
  }

  public addFirmSpecificDocumentAdditionalRecipient(
    firmId: number, firmSpecificDocumentId: number, attributes: any
  ): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/firm-specific-documents/${firmSpecificDocumentId}/add-additional-mail-recipient`,
      {data: {attributes}}
    );
  }

  public create(attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      '/api/desktop/firms/create',
      {data: {attributes}}
    );
  }

  public createBezahlDeOrg(firmId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/functions/bezahlde/create`,
      {data: {attributes}}
    );
  }

  public createFirmSpecificDocument(firmId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/firm-specific-documents/create`,
      {data: {attributes}}
    );
  }

  public delete(firmId: number): Promise<any> {
    return this.ajaxService.sendDelete(`/api/desktop/firms/${firmId}/delete`);
  }

  public deleteFirmSpecificDocument(firmId: number, firmSpecificDocumentId: number): Promise<any> {
    return this.ajaxService.sendDelete(`/api/desktop/firms/${firmId}/firm-specific-documents/${firmSpecificDocumentId}`);
  }

  public getBezahlDeOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/firms/${firmId}/functions/bezahlde/options`
    );
  }

  public getBlank(): Promise<any> {
    return this.ajaxService.getJson('/api/desktop/firms/blank');
  }

  public getBlankFirmSpecificDocument(firmId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}/firm-specific-documents/blank`);
  }

  public getCallcenterOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/callcenter/options`);
  }

  public getCarRentalCompaniesOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/car-rental-companies/options`);
  }

  public getDetailersOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/detailers/options`);
  }

  public getDifferingRepairFirmsOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/differing-repair-firms/options`);
  }

  public getFirmSpecificDocumentFollowUpOptions(firmId: number, firmSpecificDocumentId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/firms/${firmId}/firm-specific-documents/${firmSpecificDocumentId}/follow-up-options`
    );
  }

  public getLawyersOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/lawyers/options`);
  }

  public getLogisticiansOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/logisticians/options`);
  }

  public getPaintShopsOptions(firmId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/firms/${firmId}/assignments/paint-shops/options`);
  }

  public loadBezahlDeOrgCreationForm(firmId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}/functions/bezahlde/blank`);
  }

  public loadFirm(firmId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}`);
  }

  public loadFirmBranchUserAssignments(firmId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}/branch-user-assignment`);
  }

  public loadFirmSpecificDocument(firmId: number, firmSpecificDocumentId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}/firm-specific-documents/${firmSpecificDocumentId}`);
  }

  public loadFirmSpecificDocuments(firmId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/firms/${firmId}/firm-specific-documents`);
  }

  public newFirm(): Promise<any> {
    return this.ajaxService.getJson('/api/desktop/firms/new');
  }

  public reloadCreate(attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      '/api/desktop/firms/reload',
      {data: {attributes}}
    );
  }

  public reloadFirmAssignments(firmId: string): Promise<any> {
    return this.ajaxService.getJson(
      `/api/desktop/firms/${firmId}/assignments/reload`
    );
  }

  public reloadFirmBasedata(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/basedata/reload`,
      {data: {attributes}}
    );
  }

  public reloadFirmFunctions(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/functions/reload`,
      {data: {attributes}}
    );
  }

  public saveFirmAccounting(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/accounting/save`,
      {data: {attributes}}
    );
  }

  public saveFirmAppraisers(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/appraisers/save`,
      {data: {attributes}}
    );
  }

  public saveFirmAssignments(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/assignments/save`,
      {data: {attributes}}
    );
  }

  public saveFirmBankAccount(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/bank-account/save`,
      {data: {attributes}}
    );
  }

  public saveFirmBasedata(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/basedata/save`,
      {data: {attributes}}
    );
  }

  public saveFirmBranchUserAssignments(firmId: number, attributes: any): Promise<any> {
    return this.ajaxService.putJson(
      `/api/desktop/firms/${firmId}/branch-user-assignment`,
      {data: {attributes}}
    );
  }

  public saveFirmDocuments(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/documents/save`,
      {data: {attributes}}
    );
  }

  public saveFirmFunctions(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/functions/save`,
      {data: {attributes}}
    );
  }

  public saveFirmPresets(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/presets/save`,
      {data: {attributes}}
    );
  }

  public saveFirmRecordProcess(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/record-process/save`,
      {data: {attributes}}
    );
  }

  public saveFirmRemarks(firmId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/remarks/save`,
      {data: {attributes}}
    );
  }

  public saveFirmSpecificDocument(firmId: string, firmSpecificDocumentId: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/firms/${firmId}/firm-specific-documents/${firmSpecificDocumentId}`,
      {data: {attributes}}
    );
  }
}
